import { useMemo, useState } from 'react';
import { moleculeLibrary, prettyFormula } from '@chemai/engine';
import { type MessageKey, useI18n } from '../i18n';
import { useTitle } from '../router';
import { MoleculeViewer } from '../components/MoleculeViewer';
import { MoleculeBuilder } from '../components/MoleculeBuilder';
import { Segmented, Stat } from '../components/ui';
import { STARTERS, buildModel, type BuildIssue, type Sketch } from '../lib/moleculeBuilder';

type View = 'library' | 'build';

const BUILD_TEXT = {
  en: {
    view: 'Molecules', library: 'Examples', build: 'Draw your own', centers: 'Geometry around each central atom',
    noCenter: 'No central atom (a single atom or a diatomic molecule).', lp: 'lone pairs', unknownShape: 'not a standard VSEPR shape',
    empty: 'Add at least one atom to see the 3-D model.',
    disconnected: 'All atoms must be joined into one molecule. Connect the separate parts with bonds.',
    overbonded: '{a} has more bonds than it has valence electrons. Remove a bond or lower the bond order.',
    steric: '{a} has more than 6 electron domains, which is not supported.',
    radical: '{a} has an unpaired electron (incomplete valence). Add a bond or use “Add hydrogens”.',
    unusual: '{a} has more bonds than usual (expanded octet or an ion).',
  },
  ro: {
    view: 'Molecule', library: 'Exemple', build: 'Desenează propria moleculă', centers: 'Geometria în jurul fiecărui atom central',
    noCenter: 'Nu există atom central (un singur atom sau o moleculă diatomică).', lp: 'perechi neparticipante', unknownShape: 'nu este o formă VSEPR standard',
    empty: 'Adaugă cel puțin un atom pentru a vedea modelul 3D.',
    disconnected: 'Toți atomii trebuie legați într-o singură moleculă. Unește părțile separate prin legături.',
    overbonded: '{a} are mai multe legături decât electroni de valență. Elimină o legătură sau scade ordinul legăturii.',
    steric: '{a} are mai mult de 6 domenii electronice, ceea ce nu este acceptat.',
    radical: '{a} are un electron neîmperecheat (valență incompletă). Adaugă o legătură sau folosește „Completează cu hidrogen”.',
    unusual: '{a} are mai multe legături decât de obicei (octet extins sau ion).',
  },
  tr: {
    view: 'Moleküller', library: 'Örnekler', build: 'Kendi molekülünü çiz', centers: 'Her merkez atomun çevresindeki geometri',
    noCenter: 'Merkez atom yok (tek atom veya iki atomlu molekül).', lp: 'ortaklanmamış çift', unknownShape: 'standart bir VSEPR şekli değil',
    empty: '3B modeli görmek için en az bir atom ekle.',
    disconnected: 'Bütün atomlar tek bir molekülde birleşmeli. Ayrı parçaları bağlarla birleştir.',
    overbonded: '{a} atomunun bağ sayısı değerlik elektronlarından fazla. Bir bağı kaldır veya bağ derecesini düşür.',
    steric: '{a} atomunun 6’dan fazla elektron bölgesi var; bu desteklenmiyor.',
    radical: '{a} atomunda eşleşmemiş elektron var (değerlik eksik). Bağ ekle veya “Hidrojenle tamamla”yı kullan.',
    unusual: '{a} atomu normalden fazla bağ yapmış (genişlemiş oktet veya iyon).',
  },
} as const;

function issueText(issue: BuildIssue, B: (typeof BUILD_TEXT)[keyof typeof BUILD_TEXT]): string {
  if (issue.kind === 'empty' || issue.kind === 'disconnected') return B[issue.kind];
  return B[issue.kind].replace('{a}', `${issue.symbol}${issue.atom + 1}`);
}

export function MoleculesPage() {
  const { t, num, lang } = useI18n();
  useTitle(t('mol.title'));
  const B = BUILD_TEXT[lang as keyof typeof BUILD_TEXT] ?? BUILD_TEXT.en;
  const library = useMemo(() => moleculeLibrary(), []);
  const [view, setView] = useState<View>('library');
  const [id, setId] = useState('h2o');
  const [labels, setLabels] = useState(true);
  const [lonePairs, setLonePairs] = useState(true);
  const [spin, setSpin] = useState(false);
  const [sketch, setSketch] = useState<Sketch>(() => STARTERS.ethanol());
  const built = useMemo(() => buildModel(sketch), [sketch]);

  const m = library.find((x) => x.id === id) ?? library[0];
  const shape = m.vsepr ? t(`shape.${m.vsepr.shape}` as MessageKey) : t('shape.trigonal-planar');

  const toggles = (
    <div className="toggles">
      <label className="check"><input type="checkbox" checked={labels} onChange={(e) => setLabels(e.target.checked)} /> {t('mol.labels')}</label>
      <label className="check"><input type="checkbox" checked={lonePairs} onChange={(e) => setLonePairs(e.target.checked)} /> {t('mol.lonePairs')}</label>
      <label className="check"><input type="checkbox" checked={spin} onChange={(e) => setSpin(e.target.checked)} /> {t('mol.spin')}</label>
    </div>
  );

  return (
    <div className="page">
      <header className="page-head">
        <h1>{t('mol.title')}</h1>
        <p className="lead">{t('mol.lead')}</p>
      </header>
      <Segmented<View> label={B.view} value={view} onChange={setView}
        options={[{ value: 'library', label: B.library }, { value: 'build', label: B.build }]} />

      {view === 'library' ? (
        <div className="mol-layout">
          <nav className="mol-list" aria-label={t('mol.title')}>
            {library.map((x) => (
              <button key={x.id} type="button" className={x.id === id ? 'mol-btn on' : 'mol-btn'} aria-pressed={x.id === id} onClick={() => setId(x.id)}>
                {prettyFormula(x.formula)}
              </button>
            ))}
          </nav>
          <section className="panel">
            {toggles}
            <MoleculeViewer
              model={m}
              showLabels={labels}
              showLonePairs={lonePairs}
              autoRotate={spin}
              height={420}
              description={t('mol.describe', { f: m.formula, n: m.atoms.length, s: shape })}
            />
          </section>
          <aside className="panel">
            <h2 className="formula-big">{prettyFormula(m.formula)}</h2>
            <div className="stats">
              {m.vsepr && <Stat label={t('geo.notation')} value={<span className="mono">{m.vsepr.notation}</span>} />}
              <Stat label={t('geo.shape')} value={shape} emphasis />
              {m.vsepr && <Stat label={t('geo.angle')} value={m.vsepr.idealAngles.map((a) => `${num(a, 1)}°`).join(', ')} />}
              <Stat label={t('geo.polarity')} value={m.polar ? t('geo.polar') : t('geo.nonpolar')} />
              <Stat label={t('mol.atoms')} value={m.atoms.length} />
              <Stat label={t('mol.sigma')} value={m.sigmaBonds} />
              <Stat label={t('mol.pi')} value={m.piBonds} />
            </div>
            {m.vsepr?.anglesCompressed && <p className="hint">{t('geo.compressed')}</p>}
            {m.id === 'c6h6' && <p className="hint">{t('mol.aromatic')}</p>}
            <p className="muted small">{t('mol.scaleNote')}</p>
          </aside>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
          <MoleculeBuilder sketch={sketch} onChange={setSketch} lang={lang as 'en' | 'ro' | 'tr'} />
          {!built.ok ? (
            <p className={built.error.kind === 'empty' ? 'notice' : 'error'} role="status">{issueText(built.error, B)}</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
              <section className="panel" style={{ flex: '999 1 520px', minWidth: 0 }}>
                {toggles}
                <MoleculeViewer
                  key={built.model.id}
                  model={built.model}
                  showLabels={labels}
                  showLonePairs={lonePairs}
                  autoRotate={spin}
                  height={420}
                  description={t('mol.describe', {
                    f: built.model.formula,
                    n: built.model.atoms.length,
                    s: built.centers[0]?.shape ? t(`shape.${built.centers[0].shape}` as MessageKey) : '—',
                  })}
                />
              </section>
              <aside className="panel" style={{ flex: '1 1 280px', minWidth: 0 }}>
                <h2 className="formula-big">{prettyFormula(built.model.formula)}</h2>
                <div className="stats">
                  <Stat label={t('geo.polarity')} value={built.model.polar ? t('geo.polar') : t('geo.nonpolar')} emphasis />
                  <Stat label={t('mol.atoms')} value={built.model.atoms.length} />
                  <Stat label={t('mol.sigma')} value={built.model.sigmaBonds} />
                  <Stat label={t('mol.pi')} value={built.model.piBonds} />
                </div>
                <p className="h-small">{B.centers}</p>
                {built.centers.length === 0 ? (
                  <p className="muted small">{B.noCenter}</p>
                ) : (
                  <ul className="small" style={{ paddingLeft: 18, margin: '6px 0' }}>
                    {built.centers.map((c) => (
                      <li key={c.atom}>
                        <strong>{c.symbol}{c.atom + 1}</strong>: {c.shape ? t(`shape.${c.shape}` as MessageKey) : B.unknownShape}
                        {c.angles.length > 0 && ` (${c.angles.map((a) => `${num(a, 1)}°`).join(', ')})`}
                        {` · ${c.hybrid}`}
                        {c.lonePairs > 0 && ` · ${c.lonePairs} ${B.lp}`}
                      </li>
                    ))}
                  </ul>
                )}
                {built.warnings.map((w, i) => <p key={i} className="hint">{issueText(w, B)}</p>)}
                <p className="muted small">{t('mol.scaleNote')}</p>
              </aside>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
